import React, { useState, useEffect } from 'react';
import { FaCar, FaGasPump, FaTachometerAlt } from 'react-icons/fa';
import { TbSteeringWheel } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import Skeleton from '../components/ui/Skeleton';
import SearchableSelect from '../components/ui/SearchableSelect';

function Cars() {
    const [listing, setListing] = useState([]);
    const [selectedMake, setSelectedMake] = useState("");
    const [selectedModel, setSelectedModel] = useState("");
    const [makes, setMakes] = useState([]);
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [priceValue, setPrice] = useState(1000000);

    useEffect(() => {
        const fetchMakes = async () => {
            try {
                const response = await fetch('/api/listing/make');
                const data = await response.json();
                // API returns array of objects with {make, image}
                const makesArray = Array.isArray(data) ? data : [];
                // Filter out any invalid entries and log for debugging
                const validMakes = makesArray.filter(make => {
                    if (typeof make === 'string') return make.trim() !== '';
                    if (make && typeof make === 'object') {
                        return make.make && String(make.make).trim() !== '';
                    }
                    return false;
                });
                setMakes(validMakes);
            } catch (error) {
                setMakes([]);
            }
        };
        fetchMakes();
    }, []);

    useEffect(() => {
        const fetchModels = async () => {
            if (!selectedMake) return;
            try {
                const response = await fetch(`/api/listing/model?make=${selectedMake}`);
                const data = await response.json();
                // API returns array of strings, ensure it's an array
                setModels(Array.isArray(data) ? data : []);
                setSelectedModel("");
            } catch (error) {
                setModels([]);
            }
        };
        fetchModels();
    }, [selectedMake]);

    useEffect(() => {
        const fetchListings = async () => {
            setLoading(true);
            try {
                const response = await fetch('/api/listing');
                if (!response.ok) {
                    throw new Error(`API error: ${response.status}`);
                }
                let data = await response.json();
                // Ensure data is an array
                if (!Array.isArray(data)) {
                    console.error('API returned non-array:', data);
                    data = [];
                }

                if (selectedMake) {
                    data = data.filter(listing => {
                        const listingMake = listing.make ? String(listing.make).trim() : '';
                        const selectedMakeTrimmed = String(selectedMake).trim();
                        return listingMake.toLowerCase() === selectedMakeTrimmed.toLowerCase();
                    });
                }
                if (selectedModel) {
                    data = data.filter(listing => {
                        const listingModel = listing.model ? String(listing.model).trim() : '';
                        const selectedModelTrimmed = String(selectedModel).trim();
                        return listingModel.toLowerCase() === selectedModelTrimmed.toLowerCase();
                    });
                }
                if (priceValue > 0) {
                    data = data.filter(listing => {
                        if (!listing.price) return false;
                        const listingPrice = typeof listing.price === 'string' 
                            ? parseInt(listing.price.replace(/,/g, ''), 10) 
                            : parseInt(listing.price, 10);
                        return !isNaN(listingPrice) && listingPrice <= priceValue;
                    });
                }
                data = data.filter(listing => listing.visibility === "Active");
                data.sort((a, b) => new Date(b.date) - new Date(a.date));
                setListing(data);
            } catch (error) {
                // Error handled silently
            } finally {
                setLoading(false);
            }
        };
        fetchListings();
    }, [selectedMake, selectedModel, priceValue]);

    const renderSkeleton = () => (
        <div className="listingCard p-4 mb-4 rounded-lg flex flex-col gap-1 listing-card shadow-md bg-white">
            <Skeleton className="h-[180px] w-[100%] rounded-xl mb-2" />
            <Skeleton className="h-5 w-[100%] mb-1" />
            <Skeleton className="h-5 w-[60%] mb-1" />
        </div>
    );

    return (
        <div className="">
            <div className='w-full flex flex-col justify-center items-center bg-texcher1 mb-8 py-4'>
                <div className='text-center mt-3'>
                    <h1 className='text-2xl p-2 md:text-3xl font-bold md:font-medium'>
                        Filter Cars by Make, Model, and Price Range
                    </h1>
                    <p className='mt-2 p-2'>
                        Easily search for cars by selecting the make, model, and price range to streamline your filtering process.
                    </p>
                </div>

                <div className='flex justify-center gap-3 md:gap-10 items-center mt-5 flex-col md:flex-row'>
                    <div className="mb-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search Make</label>
                        <SearchableSelect
                            placeholder="Type to search makes..."
                            selectedKeys={selectedMake ? new Set([selectedMake]) : new Set()}
                            onSelectionChange={(keys) => {
                                const value = Array.from(keys)[0] || "";
                                setSelectedMake(value);
                                setSelectedModel("");
                            }}
                            options={Array.isArray(makes) ? makes.map((make) => {
                                if (typeof make === 'string') {
                                    return { value: make, label: make };
                                } else if (make && typeof make === 'object') {
                                    const makeName = make.make || make.name || String(make) || '';
                                    return { value: makeName, label: makeName };
                                } else {
                                    return { value: String(make || ''), label: String(make || '') };
                                }
                            }) : []}
                            className="max-w-xs w-80"
                        />
                    </div>

                    <div className="mb-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search Model</label>
                        <SearchableSelect
                            placeholder={selectedMake ? "Type to search models..." : "Select a make first"}
                            selectedKeys={selectedModel ? new Set([selectedModel]) : new Set()}
                            onSelectionChange={(keys) => {
                                const value = Array.from(keys)[0] || "";
                                setSelectedModel(value);
                            }}
                            options={Array.isArray(models) ? models.map((model) => {
                                if (typeof model === 'string') {
                                    return { value: model, label: model };
                                } else if (model && typeof model === 'object') {
                                    const modelName = model.model || model.name || String(model) || '';
                                    return { value: modelName, label: modelName };
                                } else {
                                    return { value: String(model || ''), label: String(model || '') };
                                }
                            }) : []}
                            className="max-w-xs w-80"
                            disabled={!selectedMake}
                        />
                    </div>

                    <div className="flex flex-col gap-0 w-full h-full max-w-md items-start justify-center mb-1">
                        <label className="text-sm font-medium text-gray-700 mb-2">Max Price: ${priceValue.toLocaleString()}</label>
                        <input
                            type="range"
                            min="0"
                            max="1000000"
                            step="10000"
                            value={priceValue}
                            onChange={(e) => setPrice(Number(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <p className="text-default-500 font-medium text-small">
                            Selected budget: ${priceValue.toLocaleString()}
                        </p>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 mx-20">
                    {Array(8).fill(null).map((_, index) => (
                        <div key={index}>{renderSkeleton()}</div>
                    ))}
                </div>
            ) : listing.length === 0 ? (
                <div className='mx-20 m-auto flex justify-center'>
                    <img className='w-96 h-96' src="/nodata.jpg" alt="" />
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mx-1 md:mx-20">
                    {listing.map(item => (
                        <div key={item.id} className="relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                            <div className="relative">
                                <div className="relative overflow-hidden bg-gray-100">
                                    <Link to={`/cars/${item._id}`}>
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full h-56 object-cover transition-transform duration-500 ease-in-out hover:scale-110"
                                        />
                                    </Link>
                                    {item.itemCondition === "Used" && (
                                        <span className='absolute top-3 left-3 bg-[#DC2626] text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg uppercase tracking-wide'>
                                            {item.itemCondition}
                                        </span>
                                    )}
                                    <div className="absolute top-3 right-3 bg-[#DC2626] px-4 py-2 rounded-full shadow-lg">
                                        <p className='text-base font-bold text-white whitespace-nowrap drop-shadow-sm'>${item.price}</p>
                                    </div>
                                </div>
                                
                                <div className="p-5">
                                    <Link to={`/cars/${item._id}`}>
                                        <h2 className="font-display text-lg font-semibold text-gray-900 mb-4 hover:text-[#DC2626] transition-colors line-clamp-2 min-h-[3.5rem]">
                                            {item.title}
                                        </h2>
                                    </Link>
                                    
                                    <div className='grid grid-cols-2 gap-3 pt-2 border-t border-gray-100'>
                                        <div className='flex items-center gap-2'>
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                                <FaGasPump className="text-[#DC2626] text-sm" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Fuel</p>
                                                <p className="text-sm text-gray-900 font-semibold truncate">{item.fuelType}</p>
                                            </div>
                                        </div>
                                        
                                        <div className='flex items-center gap-2'>
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                                <FaCar className="text-[#DC2626] text-sm" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Body</p>
                                                <p className="text-sm text-gray-900 font-semibold truncate">{item.bodyType}</p>
                                            </div>
                                        </div>
                                        
                                        <div className='flex items-center gap-2'>
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                                <FaTachometerAlt className="text-[#DC2626] text-sm" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Mileage</p>
                                                <p className="text-sm text-gray-900 font-semibold truncate">{item.mileage} {item.mileageUnit}</p>
                                            </div>
                                        </div>
                                        
                                        <div className='flex items-center gap-2'>
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                                <TbSteeringWheel className="text-[#DC2626] text-base" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Transmission</p>
                                                <p className="text-sm text-gray-900 font-semibold truncate">{item.vehicleTransmission}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default Cars;

